import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Save, Loader2, Info, Send, Building2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const templateVariables = [
    { var: '{reference_number}', desc: 'Quote reference number' },
    { var: '{customer_name}', desc: 'Contact full name' },
    { var: '{contact_first_name}', desc: 'Contact first name' },
    { var: '{company_name}', desc: 'Company name' },
    { var: '{customer_email}', desc: 'Customer email address' },
    { var: '{customer_address}', desc: 'Customer address' },
    { var: '{pack_name}', desc: 'Pack name(s) - comma separated for multi-pack' },
    { var: '{items_table}', desc: 'Line items table with quantities, prices & totals' },
    { var: '{total_price}', desc: 'Price ex VAT (formatted)' },
    { var: '{vat_amount}', desc: 'VAT amount (formatted)' },
    { var: '{total_inc_vat}', desc: 'Total inc VAT (formatted)' },
    { var: '{engineer_name}', desc: 'Engineer name' }
];

const EmailTemplates = () => {
    const { token } = useAuth();
    const [settings, setSettings] = useState({
        customer_email_subject: '',
        customer_email_body: '',
        internal_email_subject: '',
        internal_email_body: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings({
                customer_email_subject: response.data.customer_email_subject || '',
                customer_email_body: response.data.customer_email_body || '',
                internal_email_subject: response.data.internal_email_subject || '',
                internal_email_body: response.data.internal_email_body || ''
            });
        } catch (error) {
            toast.error('Failed to load email templates');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await axios.put(
                `${API}/admin/settings`,
                settings,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Email templates saved successfully');
        } catch (error) {
            toast.error('Failed to save email templates');
        } finally {
            setSaving(false);
        }
    };

    const insertVariable = (field, variable) => {
        setSettings(prev => ({
            ...prev,
            [field]: prev[field] + variable
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto animate-fade-in space-y-6">
            <div>
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">
                    Email Templates
                </h1>
                <p className="text-brand-gray-500 mt-1">
                    Customize the emails sent when quotes are generated
                </p>
            </div>

            {/* Variables Reference */}
            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
                        <Info className="h-5 w-5 text-brand-lime" />
                        Available Variables
                    </CardTitle>
                    <CardDescription>
                        Use these placeholders in your templates - they'll be replaced with actual values
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        {templateVariables.map(item => (
                            <div 
                                key={item.var}
                                className="px-3 py-1.5 bg-brand-gray-100 text-sm"
                                title={item.desc}
                            >
                                <code className="font-mono text-brand-charcoal">{item.var}</code>
                                <span className="text-brand-gray-500 ml-2 text-xs">{item.desc}</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <form onSubmit={handleSave}>
                <Tabs defaultValue="customer" className="space-y-6">
                    <TabsList className="grid w-full grid-cols-2 h-12">
                        <TabsTrigger value="customer" className="h-10 data-[state=active]:bg-brand-lime data-[state=active]:text-black">
                            <Send className="h-4 w-4 mr-2" />
                            Customer Email
                        </TabsTrigger>
                        <TabsTrigger value="internal" className="h-10 data-[state=active]:bg-brand-lime data-[state=active]:text-black">
                            <Building2 className="h-4 w-4 mr-2" />
                            Internal Email
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="customer">
                        <Card className="border-0 shadow-lg">
                            <CardHeader>
                                <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                                    <Mail className="h-5 w-5 text-brand-lime" />
                                    Customer Email Template
                                </CardTitle>
                                <CardDescription>
                                    This email is sent to the customer with the PDF quote attached
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="customer_subject" className="text-xs font-bold uppercase tracking-wide">
                                        Subject Line
                                    </Label>
                                    <Input
                                        id="customer_subject"
                                        value={settings.customer_email_subject}
                                        onChange={(e) => setSettings({ ...settings, customer_email_subject: e.target.value })}
                                        placeholder="Your Quote from Advanced Dynamics - {reference_number}"
                                        className="h-11 font-mono text-sm"
                                        data-testid="customer-email-subject"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="customer_body" className="text-xs font-bold uppercase tracking-wide">
                                        Email Body
                                    </Label>
                                    <textarea
                                        id="customer_body"
                                        value={settings.customer_email_body}
                                        onChange={(e) => setSettings({ ...settings, customer_email_body: e.target.value })}
                                        placeholder="Enter email body..."
                                        className="w-full min-h-[300px] px-3 py-2 bg-background border border-input text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                                        data-testid="customer-email-body"
                                    />
                                    <p className="text-xs text-brand-gray-400">
                                        The PDF quote will be automatically attached to this email
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="internal">
                        <Card className="border-0 shadow-lg">
                            <CardHeader>
                                <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-brand-lime" />
                                    Internal Email Template
                                </CardTitle>
                                <CardDescription>
                                    This email is sent to the sales team for every quote generated
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="internal_subject" className="text-xs font-bold uppercase tracking-wide">
                                        Subject Line
                                    </Label>
                                    <Input
                                        id="internal_subject"
                                        value={settings.internal_email_subject}
                                        onChange={(e) => setSettings({ ...settings, internal_email_subject: e.target.value })}
                                        placeholder="[Internal] New Quote Generated - {reference_number}"
                                        className="h-11 font-mono text-sm"
                                        data-testid="internal-email-subject"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="internal_body" className="text-xs font-bold uppercase tracking-wide">
                                        Email Body
                                    </Label>
                                    <textarea
                                        id="internal_body"
                                        value={settings.internal_email_body}
                                        onChange={(e) => setSettings({ ...settings, internal_email_body: e.target.value })}
                                        placeholder="Enter email body..."
                                        className="w-full min-h-[300px] px-3 py-2 bg-background border border-input text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                                        data-testid="internal-email-body"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex justify-end pt-6">
                    <Button
                        type="submit"
                        className="h-12 px-8 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide"
                        disabled={saving}
                        data-testid="save-templates-btn"
                    >
                        {saving ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <>
                                <Save className="h-5 w-5 mr-2" />
                                Save Templates
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default EmailTemplates;
